import { useTranslation } from "react-i18next"
import { DetailCheckbox } from "@/components/ui/checkbox"
import { isPrescriptionRequestType } from "@/modules/patients/detail/episode-of-care/episode-of-care-dropdown"
import { useTreatmentPlanTemplatesByIcdCodeQuery } from "@/modules/patients/detail/episode-of-care/services/useTreatmentPlanTemplatesByIcdCodeQuery"
import { matchesAsyncRequestStatus } from "@/modules/patients/detail/progress-note/lib/matches-async-request-status"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"
import { Country } from "@/types/__generated__/union-types"

type TreatmentPlanSuggestionProps = {
  country: Country
  icdCode: string
  onChange: (id: string) => void
}

const TreatmentPlanSuggestion = ({ icdCode, country, onChange }: TreatmentPlanSuggestionProps) => {
  const { t } = useTranslation()
  const treatmentPlans = useTreatmentPlanTemplatesByIcdCodeQuery(icdCode, country, {
    fetchPolicy: "network-only",
  })
  const contextAppointment = useContextAppointment()

  if (!treatmentPlans.length || !contextAppointment?.language) return null

  const isAsyncPrescription =
    contextAppointment.channel === "ASYNC_REQUEST" && isPrescriptionRequestType(contextAppointment.requestType)

  const visibleTreatmentPlans = treatmentPlans.filter((treatmentPlan) =>
    matchesAsyncRequestStatus(
      treatmentPlan.asynchronousQuestionnaireUsage,
      contextAppointment.asyncRequestStatus,
      isAsyncPrescription,
    ),
  )

  if (!visibleTreatmentPlans.length) return null

  return (
    <div className="flex flex-col gap-2">
      <label className="text-on-surface-subdued text-sm">
        {t("episodeOfCare.progressNote.treatmentPlanSuggestion")}
      </label>
      <div className="flex flex-wrap gap-2">
        {visibleTreatmentPlans.map((treatmentPlan) => (
          <DetailCheckbox
            key={treatmentPlan.id}
            data-testid={`treatment-plan-suggestion-${treatmentPlan.id}`}
            onCheckedChange={() => onChange(treatmentPlan.id)}>
            {treatmentPlan.languages.find((lang) => lang.language === contextAppointment.language)?.title}
          </DetailCheckbox>
        ))}
      </div>
    </div>
  )
}

export { TreatmentPlanSuggestion }
